import { Flag, MapPin } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import type { ProjectRecord } from '@/data/useProjects'
import { setDeliveryStatus, setProjectWaypoints, setWaypointStatus } from '@/data/waypointApi'
import { allowedTransitions, CHECKPOINT_STATUSES } from '@/lib/checkpoints'
import { suggestCorridors, type CorridorSuggestion } from '@/lib/corridors'
import { errorMessage } from '@/lib/errors'
import type { CheckpointStatus, Waypoint } from '@/types/project'
import type { Role } from '@/types/user'

const STATUS_META: Record<
  CheckpointStatus,
  { label: string; pill: string; dot: string; active: string }
> = {
  pending: {
    label: 'Not started',
    pill: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-500',
    active: 'bg-blue-600 text-white',
  },
  in_transit: {
    label: 'In transit',
    pill: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
    active: 'bg-amber-500 text-white',
  },
  reached: {
    label: 'Reached',
    pill: 'bg-green-100 text-green-700',
    dot: 'bg-green-600',
    active: 'bg-green-600 text-white',
  },
}

function buildWaypoints(corridor: CorridorSuggestion, choices: Record<number, string>): Waypoint[] {
  return corridor.steps.map((step, index) => {
    const option = step.options.find((o) => o.id === choices[index]) ?? step.options[0]
    return {
      id: option.id,
      name: option.name,
      label: step.label,
      lat: option.lat,
      lng: option.lng,
      status: 'pending',
    }
  })
}

/** Segmented status control, with statuses locked until the sequence allows them. */
function StatusControls({
  current,
  allowed,
  disabled,
  onSet,
  testidPrefix,
  ariaLabel,
}: {
  current: CheckpointStatus
  allowed: CheckpointStatus[]
  disabled: boolean
  onSet: (status: CheckpointStatus) => void
  testidPrefix: string
  ariaLabel: string
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="mt-1.5 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5"
    >
      {CHECKPOINT_STATUSES.map((status) => {
        const isActive = current === status
        const isAllowed = allowed.includes(status)
        return (
          <button
            key={status}
            type="button"
            data-testid={`${testidPrefix}-${status}`}
            disabled={disabled || !isAllowed}
            title={!isAllowed && !isActive ? 'Complete the previous leg first' : undefined}
            onClick={() => !isActive && onSet(status)}
            className={`rounded-md px-2 py-0.5 text-xs font-medium transition disabled:opacity-40 ${
              isActive ? STATUS_META[status].active : 'text-slate-600 hover:bg-white'
            }`}
          >
            {STATUS_META[status].label}
          </button>
        )
      })}
    </div>
  )
}

interface RouteCheckpointsProps {
  project: ProjectRecord
  role: Role
}

/** Route-intelligence panel: suggests border-crossing checkpoints for the
 *  corridor, lets an admin confirm them, and tracks each leg's progress. Statuses
 *  advance sequentially — only one leg can be in transit, and a leg unlocks only
 *  once the previous one is reached. */
export function RouteCheckpoints({ project, role }: RouteCheckpointsProps) {
  const isAdmin = role === 'admin'
  const waypoints = project.waypoints ?? []
  const deliveryStatus = project.deliveryStatus ?? 'pending'
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Full ordered journey: each checkpoint, then the destination arrival last.
  const sequence: CheckpointStatus[] = [...waypoints.map((wp) => wp.status), deliveryStatus]

  const suggestions = useMemo(
    () => suggestCorridors(project.originLocation, project.deliveryLocation),
    [project.originLocation, project.deliveryLocation],
  )
  const [variant, setVariant] = useState(0)
  const [choices, setChoices] = useState<Record<number, string>>({})
  const corridor = suggestions[variant]

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-700">Route checkpoints</h2>
        {isAdmin && waypoints.length > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => setProjectWaypoints(project.id, []))}
            className="text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-60"
          >
            Clear
          </button>
        )}
      </div>

      {/* Timeline: origin → checkpoints → destination */}
      <ol className="mt-4 space-y-3">
        <Endpoint
          icon={<MapPin className="h-4 w-4" />}
          title="Origin"
          subtitle={project.originLocation ?? '—'}
        />

        {waypoints.map((wp, index) => {
          const meta = STATUS_META[wp.status]
          return (
            <li key={wp.id} className="flex items-start gap-3">
              <span className={`mt-1 h-3 w-3 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-800">{wp.name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.pill}`}>
                    {meta.label}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{wp.label}</p>
                {isAdmin && (
                  <StatusControls
                    current={wp.status}
                    allowed={allowedTransitions(sequence, index)}
                    disabled={busy}
                    ariaLabel={`Set ${wp.name} status`}
                    testidPrefix={`waypoint-status-${wp.id}`}
                    onSet={(status) =>
                      run(() => setWaypointStatus(project.id, waypoints, wp.id, status))
                    }
                  />
                )}
              </div>
            </li>
          )
        })}

        <li className="flex items-start gap-3">
          <span
            className={`mt-1 h-3 w-3 shrink-0 rounded-full ${STATUS_META[deliveryStatus].dot}`}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <span className="flex items-center gap-1 text-xs font-semibold tracking-wide text-slate-400 uppercase">
              <Flag className="h-3.5 w-3.5" /> Destination
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-800">{project.deliveryLocation}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[deliveryStatus].pill}`}
              >
                {STATUS_META[deliveryStatus].label}
              </span>
            </div>
            {isAdmin && (
              <StatusControls
                current={deliveryStatus}
                allowed={allowedTransitions(sequence, waypoints.length)}
                disabled={busy}
                ariaLabel="Set destination arrival status"
                testidPrefix="delivery-status"
                onSet={(status) => run(() => setDeliveryStatus(project.id, status))}
              />
            )}
          </div>
        </li>
      </ol>

      {/* Suggestions (admin, when no checkpoints set yet) */}
      {isAdmin && waypoints.length === 0 && corridor && (
        <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/60 p-3">
          <p className="text-xs font-medium text-slate-700">
            Suggested corridor: <span className="text-primary">{corridor.name}</span>
          </p>

          {suggestions.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {suggestions.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setVariant(index)
                    setChoices({})
                  }}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    index === variant
                      ? 'bg-primary text-white'
                      : 'bg-white text-slate-600 ring-1 ring-slate-200'
                  }`}
                >
                  {option.name.replace('Copperbelt → ', '')}
                </button>
              ))}
            </div>
          )}

          <div className="mt-3 space-y-2">
            {corridor.steps.map((step, index) => (
              <div key={step.label} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-500">{step.label}:</span>
                {step.options.length > 1 ? (
                  <select
                    aria-label={step.label}
                    value={choices[index] ?? step.options[0].id}
                    onChange={(e) => setChoices((c) => ({ ...c, [index]: e.target.value }))}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
                  >
                    {step.options.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="font-medium text-slate-800">{step.options[0].name}</span>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            data-testid="add-checkpoints"
            disabled={busy}
            onClick={() =>
              run(() => setProjectWaypoints(project.id, buildWaypoints(corridor, choices)))
            }
            className="mt-3 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Add these checkpoints
          </button>
        </div>
      )}

      {waypoints.length === 0 && !isAdmin && (
        <p className="mt-3 text-sm text-slate-500">No route checkpoints have been set yet.</p>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
    </section>
  )
}

function Endpoint({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
        {icon}
      </span>
      <div>
        <span className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
          {title}
        </span>
        <p className="text-sm font-medium text-slate-700">{subtitle}</p>
      </div>
    </li>
  )
}
